import { useGetExternalAccountsSuspense } from 'api/payments/generated/fragmentPaymentsAPI';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { makePath } from 'utils/routes';

import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';

import { CreateExternalAccountModal } from './CreateExternalAccountModal';

type AccountRow = {
  id: string;
  external_id: string;
  name: string;
};

const accountSchema: ColumnDefinition<AccountRow>[] = [
  {
    title: 'Name',
    key: 'name',
    colspan: 'col-span-4',
  },
  {
    title: 'External ID',
    key: 'external_id',
    justify: 'right',
    colspan: 'col-span-2',
  },
];

const ColumnHeaders = () => (
  <div className="grid grid-flow-col auto-cols-fr gap-x-[1ch] px-f1 py-f1 text-xmd text-main-500 border-b-[0.5px] border-main-200">
    <div className="col-span-4">Name</div>
    <div className="col-span-2 text-right">External ID</div>
  </div>
);

const accountEmptyValue = {
  Name: 'No Accounts Yet',
  'External ID': 'N/A',
};

const SKELETON_MAX_ROWS = 10;

type Props = {
  workspaceId: string;
};

const TransactionListPageLoading = ({ workspaceId: _workspaceId }: Props) => {
  const [visibleRows, setVisibleRows] = useState(0);
  const directionRef = useRef(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleRows((prev) => {
        const next = prev + directionRef.current;
        if (next >= SKELETON_MAX_ROWS) {
          directionRef.current = -1;
          return SKELETON_MAX_ROWS;
        }
        if (next <= 0) {
          directionRef.current = 1;
          return 0;
        }
        return next;
      });
    }, 120);
    return () => clearInterval(timer);
  }, []);

  return (
    <PaymentViewLayout
      title="Accounts"
      primaryAction={{ title: 'New', onClick: () => {}, disabled: true }}
    >
      <div className="flex flex-col w-full">
        <div className="sticky top-0 z-10">
          <ColumnHeaders />
        </div>
        {/* eslint-disable react/no-array-index-key */}
        {Array.from({ length: visibleRows }).map((__, i) => (
          <div
            key={`skeleton-row-${i}`}
            className="h-[40px] border-b-[0.5px] border-main-200"
          />
        ))}
        {/* eslint-enable react/no-array-index-key */}
      </div>
    </PaymentViewLayout>
  );
};

const TransactionListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Recon');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const { data: accountsData } = useGetExternalAccountsSuspense();

  const accounts: AccountRow[] = accountsData.data;

  const getPath = useCallback(
    (account: AccountRow) =>
      makePath(['w', workspaceId, 'payments', 'accounts', account.id], {
        absolute: true,
      }),
    [workspaceId]
  );

  const handleCreate = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  return (
    <>
      <PaymentViewLayout
        title="Accounts"
        primaryAction={{
          title: 'New',
          onClick: handleCreate,
          'data-testid': 'create-external-account-button',
        }}
      >
        <div className="flex flex-col min-h-f1 w-full h-full">
          <div className="sticky top-0 z-10">
            <ColumnHeaders />
          </div>
          <NavTableV2
            textSize="xmd"
            data={accounts}
            emptyValue={accountEmptyValue}
            getPath={getPath}
            schema={accountSchema}
            showHeader={false}
            stickyHeader={false}
          />
        </div>
      </PaymentViewLayout>
      {showCreateModal && (
        <CreateExternalAccountModal onClose={handleCloseModal} />
      )}
    </>
  );
};

export const TransactionListPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<TransactionListPageLoading workspaceId={workspaceId} />}>
    <TransactionListPageContent workspaceId={workspaceId} />
  </Suspense>
);
